import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  Mail,
  Phone,
  User,
  Calendar,
  Eye,
  Download,
  ChevronDown,
  ChevronUp,
  Check,
  Clock,
  CheckCircle,
  Loader2,
  FileImage
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  submitted: { label: 'Przeslana', icon: Clock, color: 'bg-yellow-500/10 text-yellow-500' },
  reviewed: { label: 'Przeglądana', icon: Eye, color: 'bg-blue-500/10 text-blue-500' },
  approved: { label: 'Zatwierdzona', icon: CheckCircle, color: 'bg-green-500/10 text-green-500' },
};

export default function SelectionViewer({ galleryId, photos = [] }) {
  const [selections, setSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSelection, setExpandedSelection] = useState(null);

  useEffect(() => {
    if (galleryId) {
      fetchSelections();
    }
  }, [galleryId]);

  const fetchSelections = async () => {
    try {
      const response = await axios.get(`${API}/galleries/${galleryId}/selections`);
      setSelections(response.data);
    } catch (error) {
      console.error('Failed to fetch selections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (selectionId, newStatus) => {
    try {
      await axios.put(`${API}/selections/${selectionId}`, { status: newStatus });
      setSelections(selections.map(s =>
        s.id === selectionId ? { ...s, status: newStatus } : s
      ));
      toast.success('Status selekcji zaktualizowany');
    } catch (error) {
      toast.error('Nie udalo sie zmienic statusu');
    }
  };

  const handleExportCsv = async (selectionId) => {
    try {
      const response = await axios.get(`${API}/selections/${selectionId}/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `selection-${selectionId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Eksport CSV pobrany');
    } catch (error) {
      toast.error('Nie udalo sie wyeksportowac selekcji');
    }
  };

  const handleExportXmp = async (selectionId) => {
    try {
      const response = await axios.get(`${API}/selections/${selectionId}/export/xmp`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `selection-${selectionId}-lightroom.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Eksport XMP dla Lightroom pobrany');
    } catch (error) {
      toast.error('Nie udalo sie wyeksportowac XMP');
    }
  };

  const getSelectedPhotos = (selection) => {
    if (!selection.selected_photo_ids || !photos.length) return [];
    return photos.filter(p => selection.selected_photo_ids.includes(p.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (selections.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-12 text-center">
          <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">Brak selekcji</h3>
          <p className="text-sm text-muted-foreground">
            Klienci mogą wybierać zdjęcia z publicznej galerii
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">
          Selekcje klientow ({selections.length})
        </h3>
      </div>

      <div className="space-y-3">
        {selections.map((selection, idx) => {
          const StatusIcon = STATUS_CONFIG[selection.status]?.icon || Clock;
          const statusConfig = STATUS_CONFIG[selection.status] || STATUS_CONFIG.submitted;
          const selectedPhotos = getSelectedPhotos(selection);
          const isExpanded = expandedSelection === selection.id;

          return (
            <motion.div
              key={selection.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: idx * 0.05 }}
            >
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">{selection.client_name || 'Anonim'}</h4>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {selection.client_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {selection.client_email}
                              </span>
                            )}
                            {selection.client_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {selection.client_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-3">
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {selection.selected_photo_ids?.length || 0} zdjec
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {selection.created_at ? new Date(selection.created_at).toLocaleDateString('pl-PL', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </span>
                      </div>

                      {selection.notes && (
                        <p className="mt-2 text-sm text-muted-foreground italic">
                          "{selection.notes}"
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Select
                        value={selection.status}
                        onValueChange={(value) => handleStatusChange(selection.id, value)}
                      >
                        <SelectTrigger className={`w-36 h-8 text-xs ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="submitted">Przeslana</SelectItem>
                          <SelectItem value="reviewed">Przeglądana</SelectItem>
                          <SelectItem value="approved">Zatwierdzona</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportCsv(selection.id)}
                        title="Eksport CSV"
                      >
                        <Download className="w-4 h-4" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportXmp(selection.id)}
                        title="Eksport XMP dla Lightroom"
                        className="text-orange-500 border-orange-500/50 hover:bg-orange-500/10"
                      >
                        <FileImage className="w-4 h-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedSelection(isExpanded ? null : selection.id)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Photos */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-border">
                          <h5 className="text-sm font-medium mb-3">Wybrane zdjecia:</h5>
                          {selectedPhotos.length > 0 ? (
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                              {selectedPhotos.map((photo) => (
                                <div
                                  key={photo.id}
                                  className="aspect-square rounded overflow-hidden border border-border"
                                >
                                  <img
                                    src={photo.thumbnail_url || photo.url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Nie można załadować miniatur wybranych zdjęć
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
